/* React */
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

/* Next */
import Link from 'src/components/Link'

/* Material */
import Container from '@mui/material/Container'
import Typo from '@mui/material/Typography'
import { Theme } from '@mui/material/styles'

import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

/* Brands from preval */
import Build from './build.preval'

/* Options */
import config from 'src/config'
import { hrefForBrandPath } from 'src/functions/navigationBreadcrumbs'
import { post } from 'src/functions/fetch'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'

/* Types */
import { isBrandsResponse, tBrand } from 'src/shared/frontendTypes/brand'
import { MenuPageBuilderBrandListBlock } from 'src/shared/dbTypes/menuPageBuilder'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({

    root: {
      marginTop: theme.spacing(6),
      padding: theme.spacing(7, 4),
      backgroundColor: `var(--builder-footer-brand-bg, ${theme.palette.primary.main})`,
      color: `var(--builder-footer-brand-text, ${theme.palette.primary.contrastText})`,
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(5, 0),
      },
    },

    brandList: {
      margin: `${theme.spacing(4)} auto 0`,
      width: 'fit-content',
      maxWidth: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(3),
      flexWrap: 'wrap',
      '&.slider': {
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: theme.spacing(0, 2, 1),
        scrollSnapType: 'x proximity',
        justifyContent: 'flex-start',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        [theme.breakpoints.down('sm')]: {
          justifyContent: 'flex-start',
          paddingInline: theme.spacing(2),
        },
      },

      '& img': {
        width: 118,
        height: 68,
        objectFit: 'contain',
        filter: 'var(--builder-footer-logo-filter, none)',
        transform: 'translate(var(--builder-footer-logo-offset-x, 0px), var(--builder-footer-logo-offset-y, 0px))',
        transition: 'transform .16s ease',
      },

    },

    link: {
      display: 'grid',
      justifyItems: 'center',
      alignContent: 'center',
      gap: theme.spacing(1.25),
      position: 'relative',
      flex: '0 0 124px',
      width: 124,
      minHeight: 86,
      color: 'inherit',
      textDecoration: 'none',
      scrollSnapAlign: 'start',
      '& span': {
        fontSize: '.85rem',
        textAlign: 'center',
      },
    },

    fallbackLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 54,
      padding: theme.spacing(1, 1.5),
      border: '1px solid currentColor',
      borderRadius: 2,
      opacity: .82,
      fontWeight: 700,
      lineHeight: 1.2,
      textTransform: 'uppercase',
    },

  })
)

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

interface Props {
  title?: string
  backgroundColor?: string
  textColor?: string
  brandBlock?: MenuPageBuilderBrandListBlock
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const isLightTextColor = (value?: string) => {
  const hex = String(value || '').trim().replace('#', '')
  if (!/^[0-9a-f]{6}$/i.test(hex)) return false

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000

  return brightness > 170
}

const TopMarken: React.FC<Props> = ({
  title,
  backgroundColor,
  textColor,
  brandBlock,
  landingPageImageAiGenerated,
}) => {
  const classes = useStyles()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated],
  )

  const { brands: buildBrands } = Build
  const [loadedBrands, setLoadedBrands] = useState<tBrand[]>([])
  const requestedBrandKeys = useRef(new Set<string>())
  const selectedItems = brandBlock?.items || []
  const selectedBrandIds = brandBlock?.brandIds || []
  const brands = useMemo(() => {
    const mergedBrands = new Map<number, tBrand>()
    buildBrands.forEach(brand => mergedBrands.set(brand.id, brand))
    loadedBrands.forEach(brand => mergedBrands.set(brand.id, brand))
    return [...mergedBrands.values()]
  }, [buildBrands, loadedBrands])
  const brandsById = useMemo(() => new Map(brands.map(brand => [brand.id, brand])), [brands])
  const selectedBrands = brandBlock?.brandIds?.length
    ? selectedBrandIds
      .map(brandId => brandsById.get(brandId))
      .filter((brand): brand is tBrand => Boolean(brand))
    : brands
  const rootStyle = {
    ['--builder-footer-brand-bg' as string]: backgroundColor || undefined,
    ['--builder-footer-brand-text' as string]: textColor || undefined,
    ['--builder-footer-logo-filter' as string]: textColor && isLightTextColor(textColor)
      ? 'invert(100%)'
      : 'none',
    ['--builder-footer-logo-offset-x' as string]: `${brandBlock?.logoOffsetX ?? 0}px`,
    ['--builder-footer-logo-offset-y' as string]: `${brandBlock?.logoOffsetY ?? 0}px`,
  } as CSSProperties

  useEffect(() => {
    if (!selectedBrandIds.length) return

    const missingBrandIds = selectedBrandIds.filter(brandId => !brandsById.has(brandId))
    if (!missingBrandIds.length) return
    const missingBrandKey = missingBrandIds.join(',')
    if (requestedBrandKeys.current.has(missingBrandKey)) return

    requestedBrandKeys.current.add(missingBrandKey)

    let active = true

    post({
      url: 'brands',
      body: {
        ...config.shop,
        size: Math.max(24, selectedBrandIds.length),
        brands: selectedBrandIds,
      },
    }).then(res => {
      if (!active || !isBrandsResponse(res)) return
      if (res.brands.length === 0) return

      setLoadedBrands(current => {
        let changed = false
        const nextBrands = new Map(current.map(brand => [brand.id, brand]))
        res.brands.forEach(brand => {
          if (!nextBrands.has(brand.id)) changed = true
          nextBrands.set(brand.id, brand)
        })
        return changed ? [...nextBrands.values()] : current
      })
    }).catch(() => false)

    return () => {
      active = false
    }
  }, [brandsById, selectedBrandIds])

  return <div className={classes.root} style={rootStyle}>
    <Container maxWidth='lg'>
      <Typo component='div' variant='h6' align='center'>
        {brandBlock?.title || title || 'Unsere Top-Marken'}
      </Typo>
      
      <div className={[classes.brandList, brandBlock?.display === 'slider' ? 'slider' : ''].join(' ')}>
        {selectedItems.length > 0
          ? selectedItems.map(item =>
              <Link key={`${item.label}-${item.href}`} legacyBehavior href={item.href || '#'}>
                <a className={classes.link} title={item.label}>
                  {item.imageSrc &&
                    <>
                      <img
                        src={item.imageSrc}
                        alt={item.imageAlt || item.label}
                        width={100}
                        height={100}
                        loading='lazy'
                      />
                      <AiGeneratedBadge
                        aiGenerated={isAiGeneratedLandingImage(item.imageSrc)}
                        size='compact'
                        placement='top-left'
                      />
                    </>
                  }
                  {(brandBlock?.showLabel !== false || !item.imageSrc) &&
                    <span className={!item.imageSrc ? classes.fallbackLabel : undefined}>{item.label}</span>
                  }
                </a>
              </Link>
            )
          : selectedBrands.map(brand=>(
              <Brand
                key={brand.id}
                brand={brand}
                useColorLogo={brandBlock?.logoStyle === 'color'}
                showLabel={brandBlock?.showLabel !== false}
              />
            ))
        }
      </div>

    </Container>
  </div>
}

export default TopMarken

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

const Brand: React.FC<{
  brand: tBrand
  useColorLogo?: boolean
  showLabel?: boolean
}> = ({ brand, useColorLogo, showLabel }) => {
  const classes = useStyles()

  const logo = useColorLogo
    ? (brand.logos?.color || brand.logos?.mono)
    : (brand.logos?.mono || brand.logos?.color)
  const logoPath = logo?.xs ? `${config.image_server}/brand/${logo.xs}` : ''
  const href = hrefForBrandPath(brand.path, brand.id) || '/brands'

  return <Link legacyBehavior {...{href}}>
    <a 
      className={classes.link}
      title={brand.metaTitle}
    >
      {logoPath
        ? <img
            src={logoPath}
            alt={brand.titel}
            width={118}
            height={68}
            loading='lazy'
          />
        : <span className={classes.fallbackLabel}>{brand.titel}</span>
      }
      {showLabel && logoPath && <span>{brand.titel}</span>}
    </a>
  </Link>
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
